import React, { useMemo } from 'react';

import OnboardingModal from 'components/Globals/Accounts/Common/OnboardingModal';
import { SECTIONS, COMPONENTS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
// NOTE: we need to handle below es lint dependency cycle, currently suppressing it for git commit
// eslint-disable-next-line import/no-cycle
import EntityAbout from 'components/Globals/EntityAbout';
import { VARIANTS } from 'components/Globals/EntityDisplayCard';
import Content from 'components/Globals/Layout/StubPage/Content';
import { THEMES } from 'components/Globals/Layout/ThemeProvider';
import PhotoListing from 'components/Media/Display/PhotoListing';
import VideoListing, { VIDEO_SECTION_TYPES } from 'components/Media/Display/VideoListing';
import EntityUpcomingPerformances from 'components/Productions/Display/EntityUpcomingPerformances';
import FeaturedProductions from 'components/Productions/Display/FeaturedProductions';

import { PROFILE_TYPE_IDS, TP, SORT_OPTION_TYPES, SORT_OPTION_VALUES } from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import usePageContext from 'utils/hooks/usePageContext';
import { useQuery, useInfiniteQuery } from 'utils/react-query';

import artistQueries from 'containers/Artist/queries';
import mediaQueries from 'containers/Media/queries';

import { useTranslation } from 'src/i18n';

import PeopleAlsoViewed from '../PeopleAlsoViewed';

const TRACKING_DATA_PROPS = {
  OVERVIEW_CREDITS: {
    section: SECTIONS.OVERVIEW_CREDITS,
    component: COMPONENTS.FEATURED_PAST_PERFORMANCES,
    subComponent: SUB_COMPONENTS.PRODUCTION_SLUG,
  },
  FEATURED_VIDEOS: {
    section: SECTIONS.FEATURED_VIDEOS,
    component: COMPONENTS.MEDIA_CARD,
  },
  DIGITAL_VIDEOS: {
    section: SECTIONS.DIGITAL_VIDEOS,
    component: COMPONENTS.MEDIA_CARD,
  },
  FEATURED_PHOTOS: {
    section: SECTIONS.FEATURED_PHOTOS,
    component: COMPONENTS.MEDIA_CARD,
  },
};

const Home = ({ entityType, entity, isPreviewMode = false }) => {
  const { t } = useTranslation('NS_ENTITY_STUB_PAGE');
  const { permissions } = usePageContext();
  const { obRouteContext } = useAppContext();
  const { isDesktop } = useDeviceTypeLayouts();
  const { filterParams } = obRouteContext || {};
  const showPaidSection = entity?.stats?.isPro;

  const { data: showSimilarArtist = false } = useQuery(
    artistQueries.getArtistsWithSimilarProfessions({
      id: entity?.id,
      limit: 10,
      filters: {
        enable_ml: true,
      },
      queryConfig: {
        select: response => response?.data?.length > 0,
        enabled: !showPaidSection && !!entity?.id,
      },
    }),
  );

  // NOTE: reason of using useInfiniteQuery instead of useQuery is to get the data in same order as per ProductionListing.js and ssr level data
  const { data: hasDigitalVideos = false } = useInfiniteQuery(
    mediaQueries.getDigitalVideos({
      entityId: entity?.id,
      entityType,
      limit: 4,
      filters: {
        ...filterParams,
        sort: SORT_OPTION_TYPES.YEAR_DESC.value,
      },
      queryConfig: {
        select: response => {
          const videosCount = response?.pages?.[0]?.data?.length;

          if (isDesktop) {
            return videosCount >= 4;
          }

          return videosCount > 0;
        },
        enabled: entity?.stats?.media?.videos?.digital?.exists,
      },
    }),
  );

  const sections = useMemo(() => {
    const { stats } = entity || {};

    const components = [
      {
        component: EntityUpcomingPerformances,
        props: {
          showFilters: stats?.isPro && !isDesktop,
        },
        placement: {
          desktop: {
            column: 1,
            order: 1,
          },
          tablet: {
            column: 1,
            order: 1,
          },
          mobile: {
            column: 1,
            order: 1,
          },
        },
        showSection: stats?.productions?.future?.exists,
      },
      {
        component: VideoListing,
        props: {
          limit: 4,
          type: VIDEO_SECTION_TYPES.DIGITAL,
          trackingData: TRACKING_DATA_PROPS.DIGITAL_VIDEOS,
        },
        placement: {
          desktop: {
            column: 1,
            order: 2,
          },
          tablet: {
            column: 1,
            order: 2,
          },
          mobile: {
            column: 1,
            order: 2,
          },
        },
        sectionProps: {
          enableSeeAll: false,
        },
        showSection: hasDigitalVideos,
      },
      {
        component: PeopleAlsoViewed,
        sectionProps: {
          theme: THEMES.DARK,
          framed: true,
          noTitleChevron: true,
        },
        props: {
          cardVariant: VARIANTS.ROUNDED,
          filters: {
            enable_ml: true,
            sort: SORT_OPTION_VALUES.FEATURED_DESC,
          },
          trackingData: TRACKING_DATA_PROPS.OVERVIEW_CREDITS,
        },
        placement: {
          desktop: {
            column: 1,
            order: 3,
          },
          tablet: {
            column: 1,
            order: 3,
          },
          mobile: {
            column: 1,
            order: 3,
          },
        },
        showSection: showSimilarArtist && !showPaidSection,
      },
      {
        component: FeaturedProductions,
        props: {
          limit: 4,
          trackingData: TRACKING_DATA_PROPS.OVERVIEW_CREDITS,
        },
        placement: {
          desktop: {
            column: 1,
            order: 4,
          },
          tablet: {
            column: 1,
            order: 4,
          },
          mobile: {
            column: 1,
            order: 4,
          },
        },
        sectionProps: {
          title: t(`${TP}.FN_FEATURED_PAST_PERFORMANCES`),
          enableSeeAll: false,
        },
        showSection: stats?.productions?.past?.exists,
      },
      {
        component: VideoListing,
        props: {
          limit: 6,
          isOverview: true,
          trackingData: TRACKING_DATA_PROPS.FEATURED_VIDEOS,
        },
        placement: {
          desktop: {
            column: 1,
            order: 5,
          },
          tablet: {
            column: 1,
            order: 5,
          },
          mobile: {
            column: 1,
            order: 5,
          },
        },
        sectionProps: {
          enableSeeAll: false,
        },
        showSection:
          showPaidSection && (stats?.media?.videos?.production?.exists || stats?.media?.videos?.featured?.exists),
      },
      {
        component: PhotoListing,
        props: {
          limit: 10,
          isOverview: true,
          trackingData: TRACKING_DATA_PROPS.FEATURED_PHOTOS,
        },
        placement: {
          desktop: {
            column: 1,
            order: 6,
          },
          tablet: {
            column: 1,
            order: 6,
          },
          mobile: {
            column: 1,
            order: 6,
          },
        },
        sectionProps: {
          enableSeeAll: false,
          enableViewAllButton: false,
        },
        showSection:
          showPaidSection && (stats?.media?.images?.production?.exists || stats?.media?.images?.featured?.exists),
      },
      {
        component: EntityAbout,
        props: {
          isOverview: true,
          trackingData: {
            section: SECTIONS.BIOGRAPHY,
          },
        },
        placement: {
          desktop: {
            column: 1,
            order: 7,
          },
          tablet: {
            column: 1,
            order: 7,
          },
          mobile: {
            column: 1,
            order: 7,
          },
        },
        showSection: true,
      },
    ];

    return components;
  }, [entity, hasDigitalVideos, isDesktop, showPaidSection, showSimilarArtist, t]);

  return (
    <>
      <Content entity={entity} entityType={entityType} sections={sections} />
      {permissions?.isTargetMyProfile && !isPreviewMode && (
        <OnboardingModal profileId={entity?.id} profileTypeId={PROFILE_TYPE_IDS.ARTIST} />
      )}
    </>
  );
};

export default Home;
